import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiErr } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Label, Textarea } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import ApplyWizard from "../components/valueprint/ApplyWizard";
import ValueChainMap from "../components/valueprint/ValueChainMap";
import AttributesPanel from "../components/valueprint/AttributesPanel";
import {
  Fingerprint, Sparkles, Plus, ArrowUpCircle, ShieldAlert, Building2, Wallet,
  Gauge, Database, ClipboardList, Unlink, History, ScrollText, Check,
} from "lucide-react";
import { toast } from "sonner";

const canEditVp = (role) => ["tenant_admin", "esg_manager", "super_admin"].includes(role);

function TemplateCard({ t, canEdit, onDetach }) {
  return (
    <div className="card-flat p-4 space-y-2" data-testid={`vp-template-card-${t.code}`}>
      <div className="flex items-center justify-between gap-2">
        <Badge variant={t.role === "primary" ? "lime" : "outline"} className="text-[10px] uppercase">{t.role}</Badge>
        {canEdit && t.role === "secondary" && (
          <Button size="icon" variant="ghost" className="h-6 w-6" title="Detach template" onClick={() => onDetach(t)} data-testid={`vp-detach-${t.code}`}>
            <Unlink className="h-3 w-3 text-red-400" />
          </Button>
        )}
      </div>
      <div>
        <div className="text-[11px] text-muted-foreground">{t.industry_name}</div>
        <div className="font-display font-bold">{t.vertical_name}</div>
        <div className="font-mono text-[10px] text-muted-foreground">{t.code} · v{t.version}</div>
      </div>
      <div className="text-[11px] space-y-0.5 text-muted-foreground">
        <div>Applied {t.applied_at?.slice(0, 10)} by <span className="text-foreground">{t.applied_by}</span></div>
        {t.ai_confidence != null && <div>AI confidence: <span className="text-accent font-mono">{Math.round(t.ai_confidence * 100)}%</span></div>}
      </div>
    </div>
  );
}

function ModulesActivated({ d }) {
  const items = [
    { icon: Gauge, label: "Control Tower", to: "/", detail: `${d.provisioned.widgets} ValuePrint widgets in the dashboard catalog`, testid: "vp-mod-dashboard" },
    { icon: ShieldAlert, label: "Scenarios & Risks", to: "/risk-register", detail: `${d.provisioned.risks} vertical risks preloaded into the Risk Register`, testid: "vp-mod-risks" },
    { icon: Building2, label: "Supply Chain", to: "/suppliers", detail: `${d.provisioned.supplier_categories} supplier categories created`, testid: "vp-mod-suppliers" },
    { icon: Wallet, label: "Finance & Offset", to: "/finance-offset", detail: `${d.provisioned.financing_opportunities} fundable opportunities tagged for the Fund Finder`, testid: "vp-mod-finance" },
    { icon: Database, label: "Data Management", to: "/data", detail: `${d.attributes.filter((a) => a.enabled !== false).length} collectable attributes + download templates in the catalog`, testid: "vp-mod-data" },
    { icon: ClipboardList, label: "ESG Scorecard", to: "/esg-scorecard", detail: `${d.recommended_kpis.length} recommended KPIs available as one-click additions`, testid: "vp-mod-scorecard" },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="vp-modules-activated">
      {items.map((m) => (
        <Link key={m.label} to={m.to} className="card-flat p-4 hover:border-accent/50 transition-colors group" data-testid={m.testid}>
          <div className="flex items-center gap-2 mb-2"><m.icon className="h-4 w-4 text-accent" /><span className="text-sm font-semibold group-hover:text-accent transition-colors">{m.label}</span></div>
          <p className="text-[11px] text-muted-foreground">{m.detail}</p>
        </Link>
      ))}
    </div>
  );
}

export default function ValuePrint() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [secondaryOpen, setSecondaryOpen] = useState(false);
  const [detachTarget, setDetachTarget] = useState(null);
  const [detachReason, setDetachReason] = useState("");
  const canEdit = canEditVp(user?.role);

  const load = useCallback(() => {
    api.get("/valueprint/me").then((r) => setD(r.data)).catch(() => setD({ applied: false })).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const addKpi = async (k) => {
    try {
      await api.post("/valueprint/kpis/add", { code: k.template_code, kpi_key: k.key });
      toast.success(`“${k.name}” added to your ESG Scorecard`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const upgrade = async (code) => {
    try {
      const { data } = await api.post("/valueprint/me/upgrade", { code });
      toast.success(`Upgraded to v${data.to} — ${data.added_attributes} new attributes activated`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const detach = async () => {
    try {
      await api.post("/valueprint/detach", { code: detachTarget.code, reason: detachReason });
      toast.success("Template detached — provisioned items removed and logged");
      setDetachTarget(null); setDetachReason("");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const saveChain = async (stages, reason) => {
    try {
      await api.put("/valueprint/me/value-chain", { stages, reason });
      toast.success("Value chain updated — logged in the Override Log");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (loading) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading ValuePrint…</div>;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="valueprint-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow mb-1">Tenant Profile · Adaptive Value Chain</div>
          <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-3">
            <Fingerprint className="h-7 w-7 text-accent" /> Lestari ValuePrint
          </h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
            Your organisation's sustainability &amp; value-chain fingerprint — the industry template that tailors widgets, risks, suppliers, data attributes and KPIs across the platform.
          </p>
        </div>
        {d?.applied && canEdit && (
          <Button variant="outline" size="sm" onClick={() => setSecondaryOpen(true)} data-testid="vp-add-secondary-btn">
            <Plus className="h-3.5 w-3.5" /> Add secondary template
          </Button>
        )}
      </div>

      {!d?.applied ? (
        <ApplyWizard role="primary" onApplied={load} />
      ) : (
        <>
          {/* Upgrade prompts */}
          {d.version_history.filter((v) => v.upgrade_available).map((v) => (
            <div key={v.code} className="card-flat p-4 border-[#FFB800]/50 flex items-center justify-between gap-3 flex-wrap" data-testid={`vp-upgrade-banner-${v.code}`}>
              <div className="flex items-center gap-3">
                <ArrowUpCircle className="h-5 w-5 text-[#FFB800]" />
                <div>
                  <div className="text-sm font-semibold">Template update available — {v.vertical_name} v{v.latest_published_version}</div>
                  <div className="text-[11px] text-muted-foreground">You're on v{v.applied_version}. What's new: {v.latest_release_notes || "see version history"}</div>
                </div>
              </div>
              {canEdit && <Button size="sm" variant="primary" onClick={() => upgrade(v.code)} data-testid={`vp-upgrade-btn-${v.code}`}>Upgrade now</Button>}
            </div>
          ))}

          {/* Applied template summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {d.templates.map((t) => <TemplateCard key={t.code} t={t} canEdit={canEdit} onDetach={setDetachTarget} />)}
            <div className="card-flat p-4" data-testid="vp-summary-stats">
              <div className="label-eyebrow mb-2">Fingerprint summary</div>
              <div className="space-y-1 text-xs font-mono">
                <div className="flex justify-between"><span className="text-muted-foreground">Value-chain stages</span><span>{d.value_chain.length}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Active attributes</span><span>{d.attributes.filter((a) => a.enabled !== false).length}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Material issues</span><span>{d.material_topics.length}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Preloaded risks</span><span>{d.provisioned.risks}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Customisation</span><Badge variant="outline" className="text-[9px]" data-testid="vp-customisation-level">{d.customisation_level}</Badge></div>
              </div>
            </div>
          </div>

          <Tabs defaultValue="chain">
            <TabsList className="flex flex-wrap h-auto">
              <TabsTrigger value="chain" data-testid="vp-tab-chain">Value Chain Map</TabsTrigger>
              <TabsTrigger value="attributes" data-testid="vp-tab-attributes">Attributes</TabsTrigger>
              <TabsTrigger value="kpis" data-testid="vp-tab-kpis">Material Issues &amp; KPIs</TabsTrigger>
              <TabsTrigger value="modules" data-testid="vp-tab-modules">Modules Activated</TabsTrigger>
              <TabsTrigger value="overrides" data-testid="vp-tab-overrides">Override Log &amp; Versions</TabsTrigger>
            </TabsList>

            <TabsContent value="chain" className="mt-4">
              <ValueChainMap stages={d.value_chain} attributes={d.attributes} canEdit={canEdit} onSave={saveChain} />
            </TabsContent>

            <TabsContent value="attributes" className="mt-4">
              <AttributesPanel attributes={d.attributes} canEdit={canEdit} onChanged={load} />
            </TabsContent>

            <TabsContent value="kpis" className="mt-4 space-y-4">
              <div className="card-flat p-4">
                <div className="label-eyebrow mb-2">Material issues recommended by your template</div>
                <div className="flex flex-wrap gap-2" data-testid="vp-material-topics">
                  {d.material_topics.map((t) => <Badge key={t.topic} variant="lime" title={t.source}>{t.topic}</Badge>)}
                </div>
              </div>
              <div className="card-flat overflow-x-auto">
                <div className="px-4 pt-3 label-eyebrow">Recommended KPIs — one-click additions to the ESG Scorecard</div>
                <Table>
                  <TableHeader><TableRow><TableHead>KPI</TableHead><TableHead>Material issue</TableHead><TableHead>Unit</TableHead><TableHead>Frameworks</TableHead><TableHead>Template</TableHead><TableHead></TableHead></TableRow></TableHeader>
                  <TableBody>
                    {d.recommended_kpis.map((k) => (
                      <TableRow key={`${k.template_code}-${k.key}`} data-testid={`vp-kpi-row-${k.key}`}>
                        <TableCell>
                          <div className="text-sm font-medium">{k.name}</div>
                          <div className="text-[10px] text-muted-foreground">{k.why}</div>
                        </TableCell>
                        <TableCell className="text-xs">{k.material_issue}</TableCell>
                        <TableCell className="font-mono text-xs">{k.unit}</TableCell>
                        <TableCell><div className="flex flex-wrap gap-1">{(k.frameworks || []).map((f) => <Badge key={f} variant="outline" className="text-[9px]">{f}</Badge>)}</div></TableCell>
                        <TableCell className="text-[10px] text-muted-foreground">{k.template}</TableCell>
                        <TableCell>
                          {k.added
                            ? <Badge variant="success" className="text-[9px]"><Check className="h-2.5 w-2.5 mr-0.5" /> On scorecard</Badge>
                            : canEdit && <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => addKpi(k)} data-testid={`vp-kpi-add-${k.key}`}><Plus className="h-3 w-3" /> Add to Scorecard</Button>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="modules" className="mt-4">
              <ModulesActivated d={d} />
            </TabsContent>

            <TabsContent value="overrides" className="mt-4 space-y-4">
              <div className="card-flat p-4" data-testid="vp-version-history">
                <div className="label-eyebrow mb-2 inline-flex items-center gap-1"><History className="h-3.5 w-3.5" /> Template version history</div>
                <div className="space-y-3">
                  {d.version_history.map((v) => (
                    <div key={v.code} className="border border-border rounded-sm p-3">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="text-sm font-medium">{v.vertical_name} <span className="font-mono text-[10px] text-muted-foreground">({v.code})</span></div>
                        <div className="flex items-center gap-2">
                          <Badge variant="lime" className="text-[9px]">applied v{v.applied_version}</Badge>
                          {v.upgrade_available && <Badge variant="warning" className="text-[9px]">v{v.latest_published_version} available</Badge>}
                        </div>
                      </div>
                      <div className="mt-2 space-y-1">
                        {v.versions.map((ver) => (
                          <div key={ver.version} className="flex items-start gap-2 text-[11px]">
                            <span className="font-mono w-10 shrink-0">v{ver.version}</span>
                            <Badge variant="outline" className="text-[8px] shrink-0">{ver.status}</Badge>
                            <span className="text-muted-foreground">{ver.release_notes}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="card-flat overflow-x-auto" data-testid="vp-override-log">
                <div className="px-4 pt-3 label-eyebrow inline-flex items-center gap-1"><ScrollText className="h-3.5 w-3.5" /> Template override log — auditable customisation record</div>
                <Table>
                  <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Action</TableHead><TableHead>Change</TableHead><TableHead>Reason</TableHead><TableHead>Approved by</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {d.override_log.map((o) => (
                      <TableRow key={o.id} data-testid={`vp-override-row-${o.id}`}>
                        <TableCell className="font-mono text-[10px] whitespace-nowrap">{o.at?.slice(0, 10)}</TableCell>
                        <TableCell><Badge variant="outline" className="text-[9px]">{o.action}</Badge></TableCell>
                        <TableCell className="text-xs max-w-md">{o.detail}</TableCell>
                        <TableCell className="text-[11px] text-muted-foreground max-w-xs">{o.reason || "—"}</TableCell>
                        <TableCell className="text-[11px]">{o.approved_by || o.by}</TableCell>
                      </TableRow>
                    ))}
                    {!d.override_log.length && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground text-sm">No overrides yet — your ValuePrint is fully standard.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        </>
      )}

      {/* Secondary template dialog */}
      <Dialog open={secondaryOpen} onOpenChange={setSecondaryOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-accent" /> Add a secondary template</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">For mixed business models — a secondary template adds its attributes, risks and supplier categories on top of your primary. Your primary keeps the value-chain map.</p>
          <ApplyWizard role="secondary" compact appliedCodes={(d?.templates || []).map((t) => t.code)}
            onApplied={() => { setSecondaryOpen(false); load(); }} />
        </DialogContent>
      </Dialog>

      {/* Detach dialog */}
      <Dialog open={!!detachTarget} onOpenChange={(o) => !o && setDetachTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Detach “{detachTarget?.vertical_name}”</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">This removes the template's still-tagged risks, supplier categories and financing opportunities, and is recorded in the Override Log. Data you've collected is never deleted.</p>
          <div><Label>Reason *</Label><Textarea rows={2} value={detachReason} onChange={(e) => setDetachReason(e.target.value)} data-testid="vp-detach-reason" /></div>
          <Button variant="primary" onClick={detach} disabled={detachReason.trim().length < 5} data-testid="vp-detach-confirm">Detach &amp; log</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
